// Separable depth-aware blur over the accumulated cloud buffer.
//
// Run twice — once horizontally, once vertically — between the raymarch and the
// composite. A separable Gaussian costs O(radius) taps per pass instead of the
// O(radius²) a single-pass kernel would need, which is what makes a genuinely
// wide blur affordable here.
//
// The point is to smooth the raymarch grain uniformly across the whole deck.
// Every cloud pixel is blurred equally: cores, wisps and silhouette alike. The
// one thing preserved is the OCCLUSION mask — each tap is gated by scene depth,
// so cloud never bleeds across the airframe, a ridgeline, or anything else the
// march was clipped against.
//
// This runs on a copy, never on the temporal history: CloudLayer keeps the
// unblurred accumulation buffer for next frame's reprojection, so the blur can't
// feed back on itself and dissolve the deck.

varying vec2 vUv;

uniform sampler2D uClouds;      // premultiplied cloud buffer (source)
uniform sampler2D uSceneDepth;  // scene depth (logarithmic), read-only gate
uniform vec2      uDirection;   // one texel step: (1/w, 0) or (0, 1/h)
uniform float     uSigma;       // Gaussian sigma, in texels
uniform float     uDepthTol;    // max log-depth difference for a usable tap

#ifndef CLOUD_BLUR_TAPS
#define CLOUD_BLUR_TAPS 5
#endif

void main() {
  vec4  centre      = texture2D(uClouds, vUv);
  float centreDepth = texture2D(uSceneDepth, vUv).x;

  // Centre tap, weight 1 (exp(0)), always valid.
  vec4  sum  = centre;
  float wsum = 1.0;

  float twoSigmaSq = 2.0 * uSigma * uSigma;

  for (int i = 1; i <= CLOUD_BLUR_TAPS; i++) {
    float fi = float(i);
    float gw = exp(-(fi * fi) / twoSigmaSq);
    vec2  off = uDirection * fi;

    // Both sides of the centre share the same Gaussian weight.
    for (int s = 0; s < 2; s++) {
      vec2 suv = vUv + (s == 0 ? off : -off);

      // Depth gate. Comparing RAW logarithmic depth is what lets one tolerance
      // work at every range: equal differences in log depth are equal *ratios*
      // of eye distance, so uDepthTol means "within roughly this fraction of the
      // centre pixel's distance" whether that's 20 m or 20 km — no linearisation
      // and no uCameraFar needed. The depth is only ever read here, never
      // written or filtered, so the occlusion silhouette stays exactly as sharp
      // as the raymarch left it.
      float sd = texture2D(uSceneDepth, suv).x;
      float dw = 1.0 - smoothstep(uDepthTol * 0.5, uDepthTol, abs(sd - centreDepth));

      // Premultiplied colour is the correct space to filter in — rgb and alpha
      // average together without unpremultiplying around the blur.
      float w = gw * dw;
      sum  += texture2D(uClouds, suv) * w;
      wsum += w;
    }
  }

  gl_FragColor = sum / wsum;
}
